/**
 * Écran d'entrée : se connecter, créer un foyer, ou rejoindre celui du conjoint.
 *
 * Sobre volontairement — c'est un passage, pas une destination. Le code
 * d'invitation est mis en avant parce que c'est le chemin le moins évident et le
 * plus utile : sans lui, le conjoint reste dehors et toute la charge mentale
 * reste sur une seule personne.
 *
 * Google s'ajoute aux mots de passe, il ne les remplace pas : les comptes
 * existants continuent d'entrer comme avant, et un foyer auto-hébergé sans
 * `GOOGLE_CLIENT_ID` ne voit tout simplement pas le bouton.
 *
 * Le cas qui demande de l'attention est le troisième : une adresse Google
 * valide qui ne correspond à aucun compte. On ne crée alors RIEN tout seul —
 * créer un foyer au premier clic est le piège qui met deux conjoints dans deux
 * foyers vides séparés. On demande où les mettre, en gardant le jeton Google
 * déjà obtenu.
 */

import { useEffect, useRef, useState } from 'react';
import { House } from 'lucide-react';

import type { Famille, Utilisateur } from '@foyer/noyau';

import { useAppli } from '../lib/appli.js';
import { donnees } from '../lib/donnees.js';
import { ErreurApi } from '../lib/api.js';
import {
  CLIENT_ID_GOOGLE,
  EST_NATIF,
  chargeGoogle,
  estModeConnexionMobile,
  ouvreConnexionNavigateur,
  rendsLaMainAAppli,
} from '../lib/google.js';
import { Champ, TRAIT } from '../composants/ui.js';

type Onglet = 'connexion' | 'creation' | 'adhesion';

/** Identité Google vérifiée, à qui il manque seulement un foyer. */
interface GoogleSansFoyer {
  jetonId: string;
  email: string;
  prenom: string | null;
}

export function Connexion() {
  const { connecte } = useAppli();
  const [onglet, setOnglet] = useState<Onglet>('connexion');
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [prenom, setPrenom] = useState('');
  const [nomFoyer, setNomFoyer] = useState('');
  const [code, setCode] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);

  const [googleServeur, setGoogleServeur] = useState<boolean | null>(null);
  const [sansFoyer, setSansFoyer] = useState<GoogleSansFoyer | null>(null);
  const boutonGoogle = useRef<HTMLDivElement>(null);
  const modeMobile = estModeConnexionMobile();

  // Le serveur accepte-t-il Google ? On le demande plutôt que de le supposer :
  // un bouton qui mène à un 501 est pire que pas de bouton du tout.
  useEffect(() => {
    let vivant = true;
    donnees
      .etatServeur()
      .then((e) => vivant && setGoogleServeur(e.google))
      .catch(() => vivant && setGoogleServeur(false));
    return () => {
      vivant = false;
    };
  }, []);

  const googleDispo = googleServeur === true && (EST_NATIF || CLIENT_ID_GOOGLE !== '');

  /** Réponse de Google : on tente d'entrer, ou on demande où se mettre. */
  async function avecJetonGoogle(jetonId: string, ou: Record<string, unknown> = {}) {
    setErreur(null);
    setOccupe(true);
    try {
      const r = await donnees.connexionGoogle(jetonId, {
        ...ou,
        ...(modeMobile ? { avecJeton: true } : {}),
      });
      // En mode « connexion mobile », on ne fait pas entrer dans le site : on
      // rend le jeton à l'application. Entrer ici laisserait deux sessions
      // ouvertes — une dans le navigateur, une dans l'app — pour un seul geste.
      if (modeMobile && r.jeton) {
        rendsLaMainAAppli(r.jeton);
        return;
      }
      connecte(r.utilisateur as Utilisateur, r.famille as Famille);
    } catch (err) {
      // 404 + `inconnu` : l'identité est bonne, il manque seulement un foyer.
      const charge = err instanceof ErreurApi ? err.charge : null;
      if (charge?.inconnu) {
        setSansFoyer({ jetonId, email: charge.email as string, prenom: (charge.prenom as string) ?? null });
        setPrenom((p) => p || (charge.prenom as string) || '');
        setOnglet('adhesion');
      } else {
        setErreur((err as Error).message);
      }
    } finally {
      setOccupe(false);
    }
  }

  // ── Bouton Google du navigateur ────────────────────────────────────────────
  useEffect(() => {
    if (EST_NATIF || !googleDispo || sansFoyer) return;
    let vivant = true;
    chargeGoogle()
      .then(() => {
        if (!vivant || !window.google || !boutonGoogle.current) return;
        window.google.accounts.id.initialize({
          client_id: CLIENT_ID_GOOGLE,
          callback: (r) => void avecJetonGoogle(r.credential),
        });
        window.google.accounts.id.renderButton(boutonGoogle.current, {
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'pill',
          locale: 'fr',
        });
      })
      .catch((e: Error) => {
        if (vivant) setErreur(e.message);
      });
    return () => {
      vivant = false;
    };
    // `avecJetonGoogle` est recréée à chaque rendu : la déclarer ici
    // réinitialiserait le bouton en boucle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleDispo, sansFoyer, modeMobile]);

  async function soumets(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null);

    // Identité Google en attente de foyer : on repasse par la même route, avec
    // le MÊME jeton. En redemander un ferait réapparaître le sélecteur de compte
    // Google, comme si la première tentative avait échoué.
    if (sansFoyer) {
      await avecJetonGoogle(
        sansFoyer.jetonId,
        onglet === 'adhesion'
          ? { codeInvitation: code, prenom }
          : { creerFoyer: true, nomFoyer: nomFoyer || undefined, prenom },
      );
      return;
    }

    setOccupe(true);
    try {
      if (onglet === 'connexion') {
        const r = await donnees.connexion(email, motDePasse);
        connecte(r.utilisateur, r.famille);
      } else if (onglet === 'creation') {
        const r = await donnees.inscription({ email, motDePasse, prenom, nomFoyer: nomFoyer || undefined });
        connecte(r.utilisateur, r.famille);
      } else {
        await donnees.rejoindre({ email, motDePasse, prenom, codeInvitation: code });
        const r = await donnees.moi();
        connecte(r.utilisateur, r.famille);
      }
    } catch (err) {
      setErreur((err as Error).message);
    } finally {
      setOccupe(false);
    }
  }

  return (
    <main className="contenu" style={{ maxWidth: 460, paddingTop: 40 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <span
          style={{
            width: 40,
            height: 40,
            borderRadius: 16,
            background: 'var(--accent)',
            color: 'var(--encre-sur-accent)',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          <House strokeWidth={TRAIT} size={22} aria-hidden />
        </span>
        <h1 style={{ fontSize: 32 }}>Foyer</h1>
      </div>
      <p className="chapeau">
        {modeMobile
          ? 'Connectez-vous pour revenir dans l’application.'
          : 'Le calendrier des obligations de la maison : entretiens, santé, papiers, école. Vous déclarez votre foyer, l’application en déduit les échéances.'}
      </p>

      {/* ── Google ─────────────────────────────────────────────────────────── */}
      {googleDispo && !sansFoyer && (
        <div className="entree-google">
          {EST_NATIF ? (
            <button
              type="button"
              className="bouton bloc secondaire"
              disabled={occupe}
              onClick={() => {
                setErreur(null);
                ouvreConnexionNavigateur().catch((e: Error) => setErreur(e.message));
              }}
            >
              <LogoGoogle />
              Continuer avec Google
            </button>
          ) : (
            <div ref={boutonGoogle} style={{ display: 'flex', justifyContent: 'center' }} />
          )}
          <div className="ou">
            <span>ou</span>
          </div>
        </div>
      )}

      {/* ── Identité Google vérifiée, mais aucun foyer ─────────────────────── */}
      {sansFoyer && (
        <div className="note">
          Connecté avec <strong>{sansFoyer.email}</strong>, mais aucun compte n’existe encore pour cette
          adresse. Rejoignez le foyer de votre conjoint avec son code d’invitation, ou créez le vôtre.
        </div>
      )}

      <div className="puces" role="tablist" aria-label="Entrer dans l’application">
        {(
          [
            ['connexion', 'Se connecter'],
            ['creation', 'Créer un foyer'],
            ['adhesion', 'Rejoindre'],
          ] as [Onglet, string][]
        )
          // Avec une identité Google déjà vérifiée, « se connecter » n'a plus
          // d'objet : c'est fait. Restent les deux questions ouvertes.
          .filter(([cle]) => !sansFoyer || cle !== 'connexion')
          .map(([cle, libelle]) => (
            <button
              key={cle}
              type="button"
              role="tab"
              aria-selected={onglet === cle}
              className={`puce${onglet === cle ? ' active' : ''}`}
              onClick={() => {
                setOnglet(cle);
                setErreur(null);
              }}
            >
              {libelle}
            </button>
          ))}
      </div>

      <form className="carte" onSubmit={soumets}>
        {onglet === 'adhesion' && (
          <Champ etiquette="Code d’invitation" aide="Il est affiché dans l’écran Famille du foyer à rejoindre.">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="ABCD-2345"
              autoComplete="off"
              style={{ fontFamily: 'var(--police-mono)', letterSpacing: '0.08em' }}
              required
            />
          </Champ>
        )}

        {onglet !== 'connexion' && (
          <Champ etiquette="Prénom">
            <input value={prenom} onChange={(e) => setPrenom(e.target.value)} autoComplete="given-name" required />
          </Champ>
        )}

        {onglet === 'creation' && (
          <Champ etiquette="Nom du foyer" aide="Facultatif.">
            <input value={nomFoyer} onChange={(e) => setNomFoyer(e.target.value)} placeholder="Maine du Four" />
          </Champ>
        )}

        {/* Adresse et mot de passe n'ont plus d'objet quand Google a déjà
            authentifié : les demander ferait croire qu'il faut un second
            identifiant, alors que le compte créé n'en aura pas. */}
        {!sansFoyer && (
          <>
            <Champ etiquette="Adresse email">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
            </Champ>

            <Champ
              etiquette="Mot de passe"
              aide={
                onglet === 'connexion'
                  ? undefined
                  : 'Au moins 10 caractères. Une phrase entière fait un très bon mot de passe.'
              }
            >
              <input
                type="password"
                value={motDePasse}
                onChange={(e) => setMotDePasse(e.target.value)}
                autoComplete={onglet === 'connexion' ? 'current-password' : 'new-password'}
                required
                minLength={onglet === 'connexion' ? undefined : 10}
              />
            </Champ>
          </>
        )}

        {erreur && <div className="alerte">{erreur}</div>}

        <button type="submit" className="bouton bloc" disabled={occupe}>
          {occupe
            ? 'Un instant…'
            : onglet === 'connexion'
              ? 'Se connecter'
              : onglet === 'creation'
                ? 'Créer le foyer'
                : 'Rejoindre le foyer'}
        </button>
      </form>

      <p className="sourdine" style={{ textAlign: 'center' }}>
        Application auto-hébergée. Vos données restent sur votre serveur.
      </p>
    </main>
  );
}

/** Le G de Google, aux couleurs officielles : un aplat monochrome n'est pas autorisé. */
function LogoGoogle() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden style={{ flexShrink: 0 }}>
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.3 34.9 26.8 36 24 36c-5.3 0-9.7-3.1-11.3-7.6l-6.6 5.1C9.6 39.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C41.9 35.6 44 30.3 44 24c0-1.3-.1-2.3-.4-3.5z"
      />
    </svg>
  );
}
